import { create } from 'zustand';
import type { GamePhase, CircleState } from '@beat-games/shared-types';

interface GameStore {
  // Connection state
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  sessionId: string | null;
  errorMessage: string | null;
  errorCode: 'NO_CREDITS' | 'INSUFFICIENT_BALANCE' | 'RATE_LIMITED' | 'CONNECTION_FAILED' | 'NOT_QUALIFIED' | 'ELIMINATED' | null;

  // Game state
  phase: GamePhase;
  countdownSeconds: number | null;
  circle: CircleState | null;
  elapsed: number;
  score: number | null;
  survivalTimeMs: number | null;
  precisionError: number | null;
  scoreRank: number | null;
  bestRank: number | null;
  endReason: string | null;

  // Player state
  cursorPosition: { x: number; y: number } | null;
  isInsideCircle: boolean;
  outsideStartTime: number | null;

  // Actions
  setConnectionStatus: (status: GameStore['connectionStatus']) => void;
  setSessionId: (id: string | null) => void;
  setErrorMessage: (message: string | null) => void;
  setErrorCode: (code: GameStore['errorCode']) => void;
  setPhase: (phase: GamePhase) => void;
  setCountdown: (seconds: number | null) => void;
  setCircle: (circle: CircleState | null) => void;
  setElapsed: (elapsed: number) => void;
  setScore: (score: number | null) => void;
  setSurvivalTimeMs: (time: number | null) => void;
  setPrecisionError: (error: number | null) => void;
  setScoreRank: (rank: number | null) => void;
  setBestRank: (rank: number | null) => void;
  setEndReason: (reason: string | null) => void;
  setCursorPosition: (position: { x: number; y: number } | null) => void;
  setIsInsideCircle: (inside: boolean) => void;
  setOutsideStartTime: (time: number | null) => void;
  reset: () => void;
}

const initialState = {
  connectionStatus: 'disconnected' as const,
  sessionId: null,
  errorMessage: null,
  errorCode: null as GameStore['errorCode'],
  phase: 'waiting' as GamePhase,
  countdownSeconds: null,
  circle: null,
  elapsed: 0,
  score: null,
  survivalTimeMs: null,
  precisionError: null,
  scoreRank: null,
  bestRank: null,
  endReason: null,
  cursorPosition: null,
  isInsideCircle: true,
  outsideStartTime: null,
};

export const useGameStore = create<GameStore>((set) => ({
  ...initialState,

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setSessionId: (sessionId) => set({ sessionId }),
  setErrorMessage: (errorMessage) => set({ errorMessage }),
  setErrorCode: (errorCode) => set({ errorCode }),
  setPhase: (phase) => set({ phase }),
  setCountdown: (countdownSeconds) => set({ countdownSeconds }),
  setCircle: (circle) => set({ circle }),
  setElapsed: (elapsed) => set({ elapsed }),
  setScore: (score) => set({ score }),
  setSurvivalTimeMs: (survivalTimeMs) => set({ survivalTimeMs }),
  setPrecisionError: (precisionError) => set({ precisionError }),
  setScoreRank: (scoreRank) => set({ scoreRank }),
  setBestRank: (bestRank) => set({ bestRank }),
  setEndReason: (endReason) => set({ endReason }),
  setCursorPosition: (cursorPosition) => set({ cursorPosition }),
  setIsInsideCircle: (isInsideCircle) => set({ isInsideCircle }),
  setOutsideStartTime: (outsideStartTime) => set({ outsideStartTime }),
  reset: () => set(initialState),
}));
